<template lang="pug">
div(
    class="flex"
    :class="alignmentClasses"
)
    UniversalLink(
        :link="data.linkUrl"
        :openNewWindow="data.linkTarget === 'NEW_WINDOW'"
    )
        Picture(v-bind="pictureBindings")
</template>

<script>
import Picture from '@/components/picture'
import UniversalLink from '@/components/universalLink'
import { getMaxWidth, generateFlexAlignmentClasses } from '../../helpers'

export default {
    name: 'SvgWidget',
    components: {
        UniversalLink,
        Picture,
    },
    inject: ['$story'],
    props: {
        data: {
            type: Object,
            default() {
                return {}
            },
        },
        spaceLeft: {
            type: Object,
            default() {
                return {}
            },
        },
        forceLazyLoad: {
            type: Boolean,
            default: false,
        },
        isImportant: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            shouldLazyLoad: false,
        }
    },
    computed: {
        linkUrl() {
            if (this.data.linkUrl) return this.data.linkUrl
            return false
        },
        pictureBindings() {
            // Get the src, Width, and Height
            const imageData = this.getSvgData()
            return {
                isLazy: this.shouldLazyLoad,
                isImportant: this.isImportant,
                ...imageData,
            }
        },
        maxWidth() {
            return getMaxWidth(this.data)
        },
        alignmentClasses() {
            return generateFlexAlignmentClasses(
                this.data.mobileAlignment,
                this.data.tabletAlignment,
                this.data.desktopAlignment
            )
        },
    },
    created() {
        this.shouldLazyLoad = this.$story.shouldLazyLoad(this.forceLazyLoad)
    },
    methods: {
        // for SVGs, max width sets the size
        getResizedDimensions(width, height, maxWidth) {
            if (maxWidth === null) return { width, height }

            const ratio = maxWidth / width
            return {
                width: Math.floor(width * ratio),
                height: Math.floor(height * ratio),
            }
        },
        getSvgData() {
            const svgs = {}

            // set the source/dimensions property for each image size
            if (this.data.mobileImage.filename) {
                const src = this.$storyblok.formatSrc(this.data.mobileImage.filename)
                const { width, height } = this.$storyblok.getAssetDimensionsFromUrl(
                    this.data.mobileImage.filename
                )

                svgs.alt = this.data.mobileImage.alt
                svgs.mobileSrc = src
                svgs.mobileWidth = width
                svgs.mobileHeight = height

                svgs.tabletSrc = src
                svgs.tabletWidth = width
                svgs.tabletHeight = height

                svgs.desktopSrc = src
                svgs.desktopWidth = width
                svgs.desktopHeight = height
            }

            if (this.data.tabletImage.filename) {
                const src = this.$storyblok.formatSrc(this.data.tabletImage.filename)
                const { width, height } = this.$storyblok.getAssetDimensionsFromUrl(
                    this.data.tabletImage.filename
                )

                svgs.alt = svgs.alt || this.data.tabletImage.alt
                svgs.tabletSrc = src
                svgs.tabletWidth = width
                svgs.tabletHeight = height

                svgs.desktopSrc = src
                svgs.desktopWidth = width
                svgs.desktopHeight = height
            }

            if (this.data.desktopImage.filename) {
                const src = this.$storyblok.formatSrc(this.data.desktopImage.filename)
                const { width, height } = this.$storyblok.getAssetDimensionsFromUrl(
                    this.data.desktopImage.filename
                )

                svgs.alt = svgs.alt || this.data.desktopImage.alt
                svgs.desktopSrc = src
                svgs.desktopWidth = width
                svgs.desktopHeight = height
            }

            // handle legacy svgSize, for those the h/w at this point would likely be null
            if (this.data.mobileSvgSize?.width) {
                const { width, height } = this.data.mobileSvgSize

                svgs.mobileWidth = width
                svgs.mobileHeight = height

                svgs.tabletWidth = width
                svgs.tabletHeight = height

                svgs.desktopWidth = width
                svgs.desktopHeight = height
            }

            if (this.data.tabletSvgSize?.width) {
                const { width, height } = this.data.tabletSvgSize

                svgs.tabletWidth = width
                svgs.tabletHeight = height

                svgs.desktopWidth = width
                svgs.desktopHeight = height
            }

            if (this.data.desktopSvgSize?.width) {
                const { width, height } = this.data.desktopSvgSize

                svgs.desktopWidth = width
                svgs.desktopHeight = height
            }

            // apply any max widths, with svgs maxwidth sets the dimensions so it's not really max width like the image widget
            for (const device in this.maxWidth) {
                const { width, height } = this.getResizedDimensions(
                    svgs[`${device}Width`],
                    svgs[`${device}Height`],
                    this.maxWidth[device]
                )
                svgs[`${device}Width`] = width
                svgs[`${device}Height`] = height
            }

            return svgs
        },
    },
}
</script>
